import { apiRequestResponseMsg, validationTranslationKeys } from '@va/constants';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import {
  TrafficAlertBaselineDays,
  useAddTrafficAlertSettingsEmail,
  useGetTrafficAlertSettings,
  useRemoveTrafficAlertSettingsEmail,
  useResendTrafficAlertSettingsEmail,
  useUpdateTrafficAlertSettings,
} from '../apiClient';

const emailSchema = yup.string().email();

export type TrafficAlertComparisonPeriodOption = {
  label: string;
  value: string;
  disabled: boolean;
};

type FormValuesType = {
  trafficAlertActivated: boolean;
  trafficAlertBaselineDays: TrafficAlertBaselineDays;
};

const defaultFormValues: FormValuesType = {
  trafficAlertActivated: false,
  trafficAlertBaselineDays: 30,
};

const useTrafficAlertsPreferences = () => {
  const translate = useTranslate();
  const [emailAddress, setEmailAddress] = useState('');
  const [formError, setFormError] = useState<string>('');
  const [formValues, setFormValues] = useState<FormValuesType>(defaultFormValues);
  const { showSuccessNotification, showErrorNotification } = useAddNotification();

  const {
    data: trafficAlertSettings,
    isLoading: isTrafficAlertSettingsLoading,
    mutate: mutateTrafficAlertSettings,
  } = useGetTrafficAlertSettings();

  const allEmails = useMemo(() => trafficAlertSettings?.payload.trafficAlertEmailList ?? [], [trafficAlertSettings]);

  const { execute: addEmail } = useAddTrafficAlertSettingsEmail();
  const { execute: removeEmail } = useRemoveTrafficAlertSettingsEmail();
  const { execute: resendEmailConfirmation } = useResendTrafficAlertSettingsEmail();
  const { execute: updateTrafficAlertSettings, isSucceeded: isTrafficAlertSettingsUpdateSucceeded } =
    useUpdateTrafficAlertSettings();

  const comparisonPeriodOptions: TrafficAlertComparisonPeriodOption[] = useMemo(
    () => [
      {
        label: translate('form.trafficAlerts.comparisonPeriod.week'),
        value: String(7),
        disabled: !formValues.trafficAlertActivated,
      },
      {
        label: translate('form.trafficAlerts.comparisonPeriod.twoWeeks'),
        value: String(14),
        disabled: !formValues.trafficAlertActivated,
      },
      {
        label: translate('form.trafficAlerts.comparisonPeriod.month'),
        value: String(30),
        disabled: !formValues.trafficAlertActivated,
      },
    ],
    [formValues.trafficAlertActivated, translate],
  );

  const selectedComparisonPeriod = useMemo(() => String(formValues.trafficAlertBaselineDays), [formValues]);

  // Populate initial state for the form values
  useEffect(() => {
    if (!trafficAlertSettings) return;
    setFormValues({
      trafficAlertActivated: trafficAlertSettings.payload.trafficAlertActivated,
      trafficAlertBaselineDays: trafficAlertSettings.payload.trafficAlertBaselineDays,
    });
  }, [trafficAlertSettings]);

  const isSaveEmailButtonDisabled = useMemo(
    () => isTrafficAlertSettingsLoading || isTrafficAlertSettingsUpdateSucceeded,
    [isTrafficAlertSettingsLoading, isTrafficAlertSettingsUpdateSucceeded],
  );

  const clearEmailField = useCallback(() => {
    setEmailAddress('');
  }, []);

  const emailError = useCallback(
    (email: string) => {
      const isValid = emailSchema.isValidSync(email);
      if (!isValid) return translate(validationTranslationKeys.invalidEmail);
      return null;
    },
    [translate],
  );

  const onChangeEmail = useCallback(
    (value: string) => {
      const error = emailError(value);
      if (error) {
        setFormError(error);
      } else {
        setFormError('');
      }
      setEmailAddress(value);
    },
    [emailError],
  );

  const handleSaveEmailError = useCallback(
    (error: Error | undefined | unknown) => {
      if (!error || !(error instanceof Error)) {
        showErrorNotification();
        return;
      }

      try {
        const apiError: { code: string; details: any; message: string } = JSON.parse(error.message);

        if (apiError.message === apiRequestResponseMsg.ALREADY_EXISTING_EMAIL) {
          showErrorNotification(
            5000,
            translate('all.generalSettings.trafficAlertsSettings.notification.emailAlreadyExists'),
          );
          return;
        }

        if (apiError.message === apiRequestResponseMsg.INVALID_DATA) {
          showErrorNotification(5000, translate(validationTranslationKeys.invalidEmail));
          setFormError(translate(validationTranslationKeys.invalidEmail));
          return;
        }
      } catch (err) {
        showErrorNotification();
        return;
      }

      showErrorNotification();
    },
    [showErrorNotification, translate],
  );

  const onSaveEmail = useCallback(async () => {
    try {
      if (!emailAddress) return;
      const error = emailError(emailAddress);
      if (error) {
        setFormError(error);
        return;
      } else {
        setFormError('');
      }
      await addEmail({ email: emailAddress });
      clearEmailField();
      showSuccessNotification(3000, translate('all.generalSettings.trafficAlertsSettings.notification.addEmail'));
      mutateTrafficAlertSettings();
    } catch (err: any) {
      handleSaveEmailError(err);
    }
  }, [
    addEmail,
    clearEmailField,
    emailAddress,
    emailError,
    handleSaveEmailError,
    mutateTrafficAlertSettings,
    showSuccessNotification,
    translate,
  ]);

  const onRemoveEmail = useCallback(
    async (email: string) => {
      try {
        await removeEmail({ email });
        mutateTrafficAlertSettings();
        showSuccessNotification();
      } catch (err) {
        showErrorNotification();
      }
    },
    [mutateTrafficAlertSettings, removeEmail, showErrorNotification, showSuccessNotification],
  );

  const onResendEmailConfirmation = useCallback(
    async (email: string) => {
      try {
        await resendEmailConfirmation({ email });
        mutateTrafficAlertSettings();
        showSuccessNotification(3000, translate('all.generalSettings.trafficAlertsSettings.notification.resendEmail'));
      } catch (err) {
        showErrorNotification();
      }
    },
    [mutateTrafficAlertSettings, resendEmailConfirmation, showErrorNotification, showSuccessNotification, translate],
  );

  const handleActivateToggleChange = useCallback(
    async (value: boolean) => {
      try {
        setFormValues((prev) => ({ ...prev, trafficAlertActivated: value }));
        await updateTrafficAlertSettings({ trafficAlertActivated: value });
        mutateTrafficAlertSettings();
        showSuccessNotification();
      } catch (error) {
        showErrorNotification();
      }
    },
    [mutateTrafficAlertSettings, showErrorNotification, showSuccessNotification, updateTrafficAlertSettings],
  );

  const onComparisonPeriodChange = useCallback(
    async (value: TrafficAlertBaselineDays) => {
      try {
        setFormValues((prev) => ({ ...prev, trafficAlertBaselineDays: value }));
        await updateTrafficAlertSettings({ trafficAlertBaselineDays: value });
        mutateTrafficAlertSettings();
        showSuccessNotification();
      } catch (error) {
        showErrorNotification();
      }
    },
    [mutateTrafficAlertSettings, showErrorNotification, showSuccessNotification, updateTrafficAlertSettings],
  );

  return {
    allEmails,
    emailAddress,
    isTrafficAlertSettingsUpdateSucceeded,
    isTrafficAlertSettingsLoading,
    isSaveEmailButtonDisabled,
    formValues,
    formError,
    comparisonPeriodOptions,
    selectedComparisonPeriod,
    onSaveEmail,
    onChangeEmail,
    onRemoveEmail,
    onResendEmailConfirmation,
    handleActivateToggleChange,
    onComparisonPeriodChange,
  };
};

export default useTrafficAlertsPreferences;
